import { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  CloudArrowUp, FileArchive, TrashSimple, ArrowsClockwise,
  DownloadSimple, MagnifyingGlass, CaretRight, CaretDown, Folder,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// Round 37 — Admin Storage Manager
//
// Rebuilt to reflect the new object-storage layout:
//   • Files are grouped by their cloud folder (admission-letters,
//     payment-receipts, pod-resources, admin-documents, slides, uploads…).
//   • Each group is collapsible and sorted by upload date (newest first).
//   • The filename column shows the original filename appended with the
//     upload timestamp — the naming convention the backend enforces.
export default function AdminStoragePage() {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const [openFolders, setOpenFolders] = useState({});

  const fmtSize = (n) => {
    if (!n) return "0 B";
    const k = 1024;
    const units = ["B", "KB", "MB", "GB"];
    const i = Math.min(Math.floor(Math.log(n) / Math.log(k)), units.length - 1);
    return `${(n / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  const prettyFolder = (name) => {
    if (!name) return "Uncategorised";
    return name
      .replace(/^local\//, "Local · ")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/storage/files")
      .then(r => {
        const gs = r.data?.groups || [];
        setGroups(gs);
        setTotal(r.data?.total_bytes || 0);
        // Expand every group by default the first time.
        if (Object.keys(openFolders).length === 0) {
          const initial = {};
          gs.forEach(g => { initial[g.folder] = true; });
          setOpenFolders(initial);
        }
        setLoading(false);
      })
      .catch(e => {
        toast.error(e.response?.data?.detail || "Failed to load storage inventory");
        setLoading(false);
      });
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (label) form.append("label", label);
      await api.post("/admin/storage/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      setLabel("");
      e.target.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? This cannot be undone.`)) return;
    try {
      await api.post("/admin/storage/delete", { path: f.path });
      toast.success("File deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const openFile = (f) => {
    if (f.kind === "cloud") {
      const backend = process.env.REACT_APP_BACKEND_URL;
      window.open(`${backend}${f.url}`, "_blank");
    } else {
      toast("Local files are not directly browsable. Download from server filesystem.");
    }
  };

  const filteredGroups = useMemo(() => {
    if (!q) return groups;
    const needle = q.toLowerCase();
    return groups
      .map(g => ({
        ...g,
        files: g.files.filter(f =>
          (f.name || "").toLowerCase().includes(needle)
          || (f.path || "").toLowerCase().includes(needle)
          || (f.label || "").toLowerCase().includes(needle)
        ),
      }))
      .filter(g => g.files.length > 0);
  }, [groups, q]);

  const totalFiles = groups.reduce((n, g) => n + g.count, 0);
  const cloudCount = groups
    .filter(g => !g.folder.startsWith("local"))
    .reduce((n, g) => n + g.count, 0);
  const localCount = groups
    .filter(g => g.folder.startsWith("local"))
    .reduce((n, g) => n + g.count, 0);

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12 max-w-6xl mx-auto" data-testid="admin-storage-page">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Admin · Storage</p>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Document &amp; file storage
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Cloud files are grouped by folder and sorted by upload date. Filenames follow
            the <code className="px-1 bg-slate-100">name_timestamp</code> convention.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Card className="border border-slate-200 rounded-none"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-widest text-slate-500">Total files</p><p className="text-2xl font-bold text-slate-950 mt-1" data-testid="storage-count">{totalFiles}</p></CardContent></Card>
          <Card className="border border-slate-200 rounded-none"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-widest text-slate-500">Cloud</p><p className="text-2xl font-bold text-blue-800 mt-1" data-testid="storage-cloud-count">{cloudCount}</p></CardContent></Card>
          <Card className="border border-slate-200 rounded-none"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-widest text-slate-500">Local (legacy)</p><p className="text-2xl font-bold text-slate-700 mt-1" data-testid="storage-local-count">{localCount}</p></CardContent></Card>
          <Card className="border border-slate-200 rounded-none"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-widest text-slate-500">Total size</p><p className="text-2xl font-bold text-slate-950 mt-1" data-testid="storage-size">{fmtSize(total)}</p></CardContent></Card>
        </div>

        {/* Upload card */}
        <Card className="border border-blue-200 bg-blue-50/30 rounded-none mb-6" data-testid="admin-storage-upload-card">
          <CardContent className="p-5 space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-blue-700 font-bold">Upload to cloud</p>
              <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Add a new document
              </h2>
              <p className="text-[11px] text-slate-500 mt-1">Lands in the <b>admin-documents</b> folder with a timestamped filename.</p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs uppercase tracking-wider text-slate-600">Label (optional)</Label>
                <Input
                  value={label}
                  onChange={e => setLabel(e.target.value)}
                  placeholder="e.g. 2026 Cohort Handbook"
                  className="mt-1 rounded-none bg-white"
                  data-testid="admin-storage-label-input"
                />
              </div>
              <label
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white hover:bg-blue-900 cursor-pointer text-sm disabled:opacity-50"
                data-testid="admin-storage-upload-trigger"
              >
                <CloudArrowUp size={16} />
                {uploading ? "Uploading…" : "Pick file & upload"}
                <input
                  type="file"
                  className="hidden"
                  onChange={handleUpload}
                  disabled={uploading}
                  data-testid="admin-storage-upload-input"
                />
              </label>
              <button
                onClick={load}
                className="border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5"
                data-testid="admin-storage-refresh"
              >
                <ArrowsClockwise size={14} /> Refresh
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Search bar */}
        <div className="relative max-w-md mb-4">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by name, path, or label…"
            className="pl-9 rounded-none"
            data-testid="admin-storage-search"
          />
        </div>

        {/* File list — grouped by folder */}
        {loading ? (
          <p className="text-sm text-slate-500">Loading inventory…</p>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200">
            <FileArchive size={36} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              {q ? "No files match your search." : "No documents yet — upload above to get started."}
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-storage-groups">
            {filteredGroups.map((g) => {
              const open = openFolders[g.folder] !== false;
              return (
                <div
                  key={g.folder}
                  className="border border-slate-200 bg-white"
                  data-testid={`storage-folder-${g.folder}`}
                >
                  <button
                    onClick={() => setOpenFolders(s => ({ ...s, [g.folder]: !open }))}
                    className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left"
                    data-testid={`storage-folder-toggle-${g.folder}`}
                  >
                    {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    <Folder size={18} className={g.folder.startsWith("local") ? "text-slate-500" : "text-blue-700"} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{prettyFolder(g.folder)}</p>
                      <p className="text-[11px] text-slate-500 truncate">{g.folder}</p>
                    </div>
                    <span className="text-xs text-slate-500">{g.count} file{g.count !== 1 ? "s" : ""}</span>
                    <span className="text-xs text-slate-500">{fmtSize(g.size_bytes)}</span>
                  </button>
                  {open && (
                    <div className="border-t border-slate-100 divide-y divide-slate-100">
                      {g.files.map((f, i) => (
                        <div
                          key={(f.id || f.path) + i}
                          className="p-3 flex items-center gap-3 flex-wrap hover:bg-slate-50/40"
                          data-testid={`storage-row-${g.folder}-${i}`}
                        >
                          <FileArchive size={18} className={f.kind === "cloud" ? "text-blue-700" : "text-slate-500"} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{f.name}</p>
                            <p className="text-[11px] text-slate-500 truncate">{f.path}</p>
                            {f.label && <p className="text-[10px] text-purple-700 mt-0.5">{f.label}</p>}
                          </div>
                          <div className="flex flex-col items-end text-xs text-slate-500">
                            <span data-testid={`storage-date-${g.folder}-${i}`}>{fmtDate(f.modified_at)}</span>
                            <span>{fmtSize(f.size_bytes)}</span>
                          </div>
                          <Badge className={`rounded-none ${f.kind === "cloud" ? "bg-blue-100 text-blue-800" : "bg-slate-200 text-slate-700"}`}>
                            {f.kind === "cloud" ? "cloud" : "local"}
                          </Badge>
                          {f.kind === "cloud" && (
                            <button
                              onClick={() => openFile(f)}
                              className="px-2 py-1 border border-slate-300 hover:bg-slate-50 text-xs inline-flex items-center gap-1"
                              data-testid={`storage-open-${g.folder}-${i}`}
                            >
                              <DownloadSimple size={12} /> Open
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(f)}
                            className="px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 text-xs inline-flex items-center gap-1"
                            data-testid={`storage-delete-${g.folder}-${i}`}
                          >
                            <TrashSimple size={12} /> Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
